
import React, { useState, useEffect } from 'react';
import { User, ArrowRight } from 'lucide-react';
import { AppNode } from '../../../types';

interface UserTaskSimulationFormProps {
  node: AppNode;
  onNext: (data: any) => void;
}

export const UserTaskSimulationForm: React.FC<UserTaskSimulationFormProps> = ({ node, onNext }) => {
  const [formData, setFormData] = useState<Record<string, any>>({});

  useEffect(() => {
    // Initialize form data with all field keys so untouched fields are included in submission
    const fields = node.data.formSchema?.fields || [];
    const initialData: Record<string, any> = {};
    fields.forEach((field: any) => {
      if (field.key) {
        initialData[field.key] = field.type === 'boolean' ? false : '';
      }
    });
    setFormData(initialData);
  }, [node.id]);

  const fields = node.data.formSchema?.fields || [];

  const handleChange = (key: string, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNext(formData);
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-900">
      <div className="flex-1 overflow-y-auto p-5">
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
            <User size={12} />
            <span>
                {node.type === 'FormStartEvent' ? 'Start Form' : 'User Input'}
            </span>
          </div>
          
          <form id="user-task-form" onSubmit={handleSubmit} className="space-y-4">
            {fields.map((field: any, idx: number) => (
              <div key={idx} className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                
                {field.type === 'select' ? (
                  <select 
                    className="w-full px-3 py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-950 border-slate-300 dark:border-slate-700 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 transition-all"
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    value={formData[field.key] || ''}
                    required={field.required}
                  >
                    <option value="">Select...</option>
                    {field.options?.split(',').map((opt: string) => (
                      <option key={opt.trim()} value={opt.trim()}>{opt.trim()}</option>
                    ))}
                  </select>
                ) : field.type === 'textarea' ? (
                   <textarea
                    className="w-full px-3 py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-950 border-slate-300 dark:border-slate-700 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 transition-all"
                    placeholder={field.placeholder}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    value={formData[field.key] || ''}
                    required={field.required}
                    rows={4}
                   />
                ) : (
                  <input 
                    type={field.type || 'text'} 
                    className="w-full px-3 py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-950 border-slate-300 dark:border-slate-700 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 transition-all"
                    placeholder={field.placeholder}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    value={formData[field.key] || ''}
                    required={field.required}
                  />
                )}
              </div>
            ))}
            
            {fields.length === 0 && (
              <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 text-center text-slate-400 py-4 italic text-xs">
                No form fields configured.
              </div>
            )}
          </form>
        </div>
      </div>

      <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 flex justify-end">
        <button 
          form="user-task-form"
          type="submit"
          className="w-full px-5 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-bold flex items-center justify-center gap-2 shadow-sm transition-all hover:translate-y-px text-sm"
        >
          {node.type === 'FormStartEvent' ? 'Start Workflow' : 'Complete Task'}
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};
